  import React from 'react';
import { useState, useEffect, useFormContext, Hooks, Code, Form, Field, Name, Title, Info, Actions, Toast, API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { Item, itemKeys } from 'models/ItemModel';
import { Inventory, inventoryKeys } from 'models/InventoryModel';
import { CommonDataColumn } from 'models/BaseModel';
import AsyncSelect from 'react-select/async';
import { ItemInventory } from 'models/ItemInventoryModel';
import { Product } from 'models/ProductModel';
import { ChangeEvent } from 'react';


const PurchaseItem: React.FC = () => {
  const [formId, formName] = [Code.Form.Id.ITEM, Code.Form.Name.ITEM];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [controlOptions, setControlOptions] = Hooks.Form.OptionMore();
  const [formData, setFormData] = useState<ItemInventory>({});
  const [productData, setProductData] = useState<Product>({});
  const [inventoryData, setInventoryData] = useState<CommonDataColumn>({});
  
  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
  
  useEffect(() => {
    API.Data.GetByKeyId<ItemInventory>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
        setActiveId(data.item_active); 
        //setInventoryId(data.inventory_id);
        Files.List.ParseCommonData(data.inventory_data, setInventoryData);
        //const productName = await API.Data.GetProductName(data.product_id);
        const product = await API.Data.GetProductViewById<Product>(data.product_id);
        if(product){
          setProductData(product);
        }
        const formData = await Form.Map.Data(data);
        setFormData((prevData) => ({
          ...prevData,
          ...formData,
          //product_name: Utility.Value.Text(productName), 
        }));
      }})
      setLoading(false);
  }, []);

  useEffect(()=>{
    const specificationId = formData.specification_id;
    if(specificationId && specificationId >=100){
        Files.Json.APIMore(Code.File.CONTROL+specificationId, Code.Option.Common.NOTHING, setControlOptions, setError);
    }
  },[formData.specification_id])

  useEffect(()=>{
    if(productData){
      setFormData((prevData) => ({
        ...prevData,
        product_name: Utility.Value.Text(productData.product_name), 
      }));
    }
  },[productData])


  useEffect(()=>{
    //console.log("FormData changed", formData);
  },[formData])

  const onSubmit = async (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;
    if (formData) {
      const finalData = Utility.Sanitize.FormData({
        ...formData,
        inventory_data: JSON.stringify(Utility.Clean.Object(inventoryData)),
        inventory_cost_price: formData.item_net_amount,
      });

      const localItemData = Utility.Get.ModelFields<Item>(finalData, itemKeys);
      const localInventoryData = Utility.Get.ModelFields<Inventory>(finalData, inventoryKeys);

      const itemId = formData.item_id?.toString();
      const inventoryId = formData.inventory_id?.toString();

      //console.log("Final Data to Save:", localItemData, localInventoryData);

      if(localItemData && localInventoryData && itemId && inventoryId){
        const response = await API.Data.SaveItemInventory(itemId, inventoryId, localItemData, localInventoryData);
        if(response){
          if(response['code']==200){
            Utility.Message.Info(["Item updated successfully"],setMessage);
            await Utility.Set.Delay(1000);
            Utility.Button.PopupClose();
          }
        }
      }
    }
  };
  
  const onGrossChange = (updatedField: Partial<typeof formData>) => {
    setFormData((prevData) => {
      const merged = { ...prevData, ...updatedField };

      let grossAmount = Number(merged.item_gross_amount) || 0;
      let discountPercentage = Number(merged.item_discount_percentage) || 0;
      let discountAmount = Number(merged.item_discount_amount) || 0;
      let taxableAmount = Number(merged.item_taxable_amount) || 0;
      let taxPercentage = Number(merged.item_tax1_percentage) || 0;
      let taxAmount = Number(merged.item_tax1_amount) || 0;
      let netAmount = Number(merged.item_net_amount) || 0;

      let inventorySalePrice = 0;
      let inventoryPurchasePrice = 0;
    
      if (grossAmount > 0) {
        const productMarginRate = Number(productData.product_margin_rate) || 0;
        inventorySalePrice = grossAmount * productMarginRate;
        inventoryPurchasePrice = grossAmount;
        taxableAmount = grossAmount;
      }

      if (discountPercentage > 0) {
        discountAmount = (grossAmount * discountPercentage) / 100;
      }

      if (discountAmount > 0) {
        taxableAmount = grossAmount - discountAmount;
      }

      if(taxableAmount>0){
        netAmount = taxableAmount;
      }

      if (taxPercentage > 0) {
        taxAmount = (taxableAmount * taxPercentage) / 100;
      }

      if (taxAmount > 0) {
        netAmount = taxableAmount + taxAmount;
      }

      return {
        ...merged,
        inventory_sale_price: Utility.Calculate.roundOffBy5(inventorySalePrice),
        inventory_purchase_price: Utility.Maths.toDecimal2(inventoryPurchasePrice),
        item_discount_amount: Utility.Maths.toDecimal2(discountAmount),
        item_taxable_amount: Utility.Maths.toDecimal2(taxableAmount),
        item_tax1_amount: Utility.Maths.toDecimal2(taxAmount),
        item_net_amount: Utility.Maths.toDecimal2(netAmount),
      };
    });
  };

  const onTaxableChange = (updatedField: Partial<typeof formData>) => {
    setFormData((prevData) => {
      const merged = { ...prevData, ...updatedField };

      let taxableAmount = Number(merged.item_taxable_amount) || 0;
      let taxPercentage = Number(merged.item_tax1_percentage) || 0;
      let taxAmount = Number(merged.item_tax1_amount) || 0;
      let netAmount = Number(merged.item_net_amount) || 0;

      if(taxableAmount>0){
        netAmount = taxableAmount;
      }

      if (taxPercentage > 0) {
        taxAmount = (taxableAmount * taxPercentage) / 100;
      }

      if (taxAmount > 0) {
        netAmount = taxableAmount + taxAmount;
      }
   
      return {
        ...merged,
        item_taxable_amount: Utility.Maths.toDecimal2(taxableAmount),
        item_tax1_amount: Utility.Maths.toDecimal2(taxAmount),
        item_net_amount: Utility.Maths.toDecimal2(netAmount),
      };
    });
  };

  const handleGrossChange = (e: ChangeEvent<HTMLInputElement>) => {
    onGrossChange({item_gross_amount: Utility.Maths.toDecimal2(e.target.value) });
  };

  const handleDiscountPercentageChange = (e: ChangeEvent<HTMLInputElement>) => {
    onGrossChange({ item_discount_percentage: Utility.Maths.toDecimal2(e.target.value) });
  };

  const handleDiscountAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    onGrossChange({ item_discount_amount: Utility.Maths.toDecimal2(e.target.value) });
  };

  const handleTaxableChange = (e: ChangeEvent<HTMLInputElement>) => {
    if(formData.item_discount_percentage==0 && formData.item_discount_amount==0){
      onTaxableChange({ item_taxable_amount: Utility.Maths.toDecimal2(e.target.value) });
    }
  };

  const handleTaxPercentageChange = (e: ChangeEvent<HTMLInputElement>) => {
    onTaxableChange({ item_tax1_percentage: Utility.Maths.toDecimal2(e.target.value) });
  };

  const handleTaxAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    onTaxableChange({ item_tax1_amount: Utility.Maths.toDecimal2(e.target.value) });
  };

  const handlenetAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    if(formData.item_tax1_percentage==0 && formData.item_tax1_amount==0){
      setFormData((prevData) => ({
        ...prevData,
        item_net_amount: Utility.Maths.toDecimal2(e.target.value),
      })); 
    }
  };


  // const onGrossChange = () => {
  //   let grossAmount = Number(formData.item_gross_amount) || 0;
  //   let saleAmount = 0;
  //   if(grossAmount>0){
  //     let productMarginRate = Number(productData.product_margin_rate) || 0;
  //     saleAmount = grossAmount * productMarginRate;
  //     setFormData((prevData) => ({
  //       ...prevData,
  //       inventory_sale_price:  Utility.Calculate.roundOffBy5(saleAmount),
  //       inventory_purchase_price: Utility.Maths.toDecimal2(grossAmount),
  //     })); 
  //   }
  // }

  // const onDiscountPercentageChange = () => {
  //   let discountPercentage = Number(formData.item_discount_percentage) || 0;
  //   if(discountPercentage>0){
  //     let grossAmount = Number(formData.item_gross_amount) || 0;
  //     let discountAmount = (grossAmount * discountPercentage) / 100;
  //     setFormData((prevData) => ({
  //       ...prevData,
  //       item_discount_amount: Utility.Maths.toDecimal2(discountAmount),
  //     }));
  //   }
  // }

  // const onDiscountAmountChange = () => {
  //   let discountAmount = Number(formData.item_discount_amount) || 0;
  //   let grossAmount = Number(formData.item_gross_amount) || 0;
  //   let taxableAmount = grossAmount; 
  //   if(discountAmount>0){
  //     taxableAmount = grossAmount - discountAmount;
  //     setFormData((prevData) => ({
  //       ...prevData,
  //       item_taxable_amount: Utility.Maths.toDecimal2(taxableAmount)
  //     }));
  //   }
  // };

  // const onCalculateTaxable = () => {
  //   let grossAmount = Number(formData.item_gross_amount) || 0;
  //   let discountAmount = Number(formData.item_discount_amount) || 0;
  //   let taxableAmount = grossAmount - discountAmount; 
  //   if(taxableAmount>0){
  //     setFormData((prevData) => ({
  //       ...prevData,
  //       item_taxable_amount: Utility.Maths.toDecimal2(taxableAmount)
  //     }));
  //   }
  // };
 

  // const onTaxPercentageChange = () => {
  //   let taxPercentage = Number(formData.item_tax1_percentage) || 0;
  //   if(taxPercentage>0){
  //     let taxableAmount = Number(formData.item_taxable_amount) || 0;
  //     let tax1Amount = (taxableAmount * taxPercentage) / 100;
  //     setFormData((prevData) => ({
  //       ...prevData,
  //       item_tax1_amount: Utility.Maths.toDecimal2(tax1Amount),
  //     }));
  //   }
  // }

  // const onTax1AmountChange = () => {
  //   let taxableAmount = Number(formData.item_taxable_amount) || 0;
  //   let tax1Amount = Number(formData.item_tax1_amount) || 0;
  //   let netAmount = taxableAmount;
  //   if(taxableAmount>0 && tax1Amount>0){
  //     netAmount = taxableAmount + tax1Amount;
  //     setFormData((prevData) => ({
  //       ...prevData,
  //       item_net_amount: Utility.Maths.toDecimal2(netAmount)
  //     }));
  //   }
  // };

  // const onNetAmountChange = () => {
  //   let netAmount = Number(formData.item_net_amount) || 0;
  //   setFormData((prevData) => ({
  //     ...prevData,
  //     inventory_cost_price: Utility.Maths.toDecimal2(netAmount)
  //   }));
  // };

  


  // useEffect(()=>{ 
  //   onDiscountPercentageChange();
  //   onCalculateTaxable();
  // },[formData.item_discount_percentage])

  // useEffect(()=>{ 
  //   onDiscountAmountChange();
  //   onCalculateTaxable();
  // },[formData.item_discount_amount])

  // useEffect(()=>{ 
  //   onTaxPercentageChange();
  //   onTax1AmountChange();
  // },[formData.item_taxable_amount])

  // useEffect(()=>{ 
  //   onTaxPercentageChange();
  // },[formData.item_tax1_percentage])

  // useEffect(()=>{ 
  //   onTax1AmountChange();
  // },[formData.item_tax1_amount])

  // useEffect(()=>{ 
  //   onNetAmountChange();
  // },[formData.item_net_amount])
  

  async function handleProductChange(product: OptionType | null ): Promise<void> {
    if (!product) return;
    const specificationId = await API.Data.SpecificationId(product.value);
    if (!specificationId) return;

    setFormData((prev: any) => ({
      ...prev,
      product_id: product.value,
      product_name: product.label,
      specification_id: specificationId, 
    }));

  }


  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} formName={formName} formId={formId} Privilege={Privilege} />
          <Control.Tab.Header titles={["Item Details"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
            
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.Header id={Name.Product.Id} title={Title.Product} require={true}  size={[0,67,1]}>
                  <AsyncSelect id={Name.Product.Id} loadOptions={(inputValue) => API.Data.AsyncOption(inputValue, Code.Form.Name.PRODUCT, Code.Mapping.product.value, Code.Mapping.product.label)} styles={Code.Style.FormOption} onChange={(selected: OptionType | null) => handleProductChange(selected)} value={Utility.Value.Async(formData.product_id,formData.product_name)} placeholder="Search" isDisabled={false} />
                </Field.Header>
                <Field.Text id={Name.Inventory.SerialNo} value={formData.inventory_serialno} title={Title.Serial} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,33,0]} />
                {/* <Field.Date id={Name.Item.Date} value={formData.item_date} title={Title.Date} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} require={true} currentDate={true} /> */}
              </Control.Row>
              <Control.Row>
                <Field.Text id={Name.Item.SKU} value={formData.item_skucode} title={Title.Skucode} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,33,1]} disabled={true} />
                <Field.Amount id={Name.Item.Gross} value={formData.item_gross_amount} title={"Wholesale Price"} onChange={(e) => handleGrossChange(e)} size={[1,34,1]} />
                <Field.Amount id={Name.Inventory.Sale} value={formData.inventory_sale_price} title={"Sale Price"} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,33,0]}  />
              </Control.Row>
              <Control.Row>
                <Field.Amount id={Name.Item.DiscountP} value={formData.item_discount_percentage} title={"Discount %"} onChange={(e) => handleDiscountPercentageChange(e)} size={[0,33,1]} className='icon-percent' />
                <Field.Amount id={Name.Item.DiscountA} value={formData.item_discount_amount} title={"Discount Amt."} onChange={(e) => handleDiscountAmountChange(e)} size={[1,34,1]} />
                <Field.Amount id={Name.Item.Taxable} value={formData.item_taxable_amount} title={"Net Cost"} onChange={(e) => handleTaxableChange(e)} size={[1,33,0]}/>
              </Control.Row>
              <Control.Row>
                <Field.Amount id={Name.Item.Tax1P} value={formData.item_tax1_percentage} title={"Tax %"} onChange={(e) => handleTaxPercentageChange(e)} size={[0,33,1]} className='icon-percent' />
                <Field.Amount id={Name.Item.Tax1A} value={formData.item_tax1_amount} title={"Tax Amt."} onChange={(e) => handleTaxAmountChange(e)} size={[1,34,1]} />
                <Field.Amount id={Name.Item.Net} value={formData.item_net_amount} title={"Total Cost"} onChange={(e) => handlenetAmountChange(e)} size={[1,33,0]} />
              </Control.Row>
              {controlOptions.length == 12 && (
                <>
                  <Field.Title title='Specifications'/>
                  <Control.Flex>
                    <Control.Column size={[0,50,1,1]}>
                      <Control.Box className='mt2'>
                        {controlOptions.slice(0, 6).map((item, index) => (
                          <Field.Suggestion key={item.value ?? index} id={`D${item.value}`} value={Utility.Get.FieldValue(inventoryData, `D${item.value}` as keyof CommonDataColumn)} title={item.label} onChange={(e) => Form.MultiValue.Change(e, inventoryData, setInventoryData)}  dataList={item.data1} horizontal={true} className='htext2' width={70} />
                        ))}
                      </Control.Box>
                    </Control.Column>
                    <Control.Column size={[1,50,0,2]}>
                      <Control.Box className='mt2'>
                        {controlOptions.slice(6).map((item, index) => (
                          <Field.Suggestion key={item.value ?? index} id={`D${item.value}`} value={Utility.Get.FieldValue(inventoryData, `D${item.value}` as keyof CommonDataColumn)} title={item.label} onChange={(e) => Form.MultiValue.Change(e, inventoryData, setInventoryData)}  dataList={item.data1} horizontal={true} className='htext2' width={70} />
                        ))}
                      </Control.Box>
                    </Control.Column>
                  </Control.Flex>
                </>
              ) }
              {controlOptions.length !== 12 && (
                <>
                  <Field.Title title='Specifications'/>
                  <Control.Box className='mt2'>
                    {controlOptions.map((item, index) => (
                      <Field.Suggestion key={item.value ?? index} id={`D${item.value}`} value={Utility.Get.FieldValue(inventoryData, `D${item.value}` as keyof CommonDataColumn)} title={item.label} onChange={(e) => Form.MultiValue.Change(e, inventoryData, setInventoryData)}  dataList={item.data1} horizontal={true} className='htext2' width={80} />
                    ))}
                  </Control.Box>
                </>
              ) }
              {/* <Control.Box>
                {controlOptions.map((item, index) => (
                   <Field.Suggestion key={item.value ?? index} id={`D${item.value}`} value={Utility.Get.FieldValue(inventoryData, `D${item.value}` as keyof CommonDataColumn)} title={item.label} onChange={(e) => Form.MultiValue.Change(e, inventoryData, setInventoryData)}  dataList={item.data1} horizontal={true} className='htext2' width={80} />
                ))}
              </Control.Box> */}
              <Control.Row>
                <Field.Area id={Name.Item.Comment} value={formData.item_comment} title={Title.Comment} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Row>
            </Control.Tab.Content>

          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default PurchaseItem;